import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { ALFRProps, getALFilterRequest } from "./filter-obj";
import {
  GetAccountListProps,
  getAccounts,
  getSaldoEnCvu,
} from "@/services/accounts.service";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { NavigateFunction } from "react-router-dom";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("social_reason", "");
  setValue("balance_control", []);
  setValue("account_type", "");
  setValue("locality", "");
  setValue("commercial_zone", "");
  setValue("account_number", "");

  submitFilters?.(getValues());
  setFiltersApplied(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchAccounts: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchAccounts,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetAccountListProps> = getALFilterRequest(
      event as ALFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchAccounts(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
      throw error;
    }
  }
};

interface FetchAccountsHandlerProps {
  setLoadingAccounts: Dispatch<SetStateAction<boolean>>;
  setAccounts: Dispatch<SetStateAction<any>>;
  filters: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad: boolean;
}

export const fetchAccountsHandler = async ({
  setLoadingAccounts,
  setAccounts,
  filters,
  navigate,
  fetchPermissions,
  showLoad,
}: FetchAccountsHandlerProps) => {
  try {
    if (showLoad) setLoadingAccounts(true);
    const response = await getAccounts({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setAccounts(response);

    setLoadingAccounts(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudo cargar el listado de cuentas.",
      });
    }
    throw error;
  }
};

export interface CVUBalanceResponse {
  cvu: string;
  saldo: string;
}

const saldoRequestByCvu = new Map<string, Promise<unknown>>();

interface FetchCVUBalanceHandlerProps {
  cvu?: string | null;
  isCancelled: () => boolean;
  setLoadingCVUBalance: Dispatch<SetStateAction<boolean>>;
  setCvuBalance: Dispatch<SetStateAction<CVUBalanceResponse | null>>;
  setCvuBalanceError: Dispatch<SetStateAction<boolean>>;
}

export const fetchCVUBalanceHandler = async ({
  cvu,
  isCancelled,
  setLoadingCVUBalance,
  setCvuBalance,
  setCvuBalanceError,
}: FetchCVUBalanceHandlerProps) => {
  if (!cvu) return;

  setLoadingCVUBalance(true);
  setCvuBalanceError(false);

  try {
    let requestPromise = saldoRequestByCvu.get(cvu);
    if (!requestPromise) {
      requestPromise = getSaldoEnCvu({ cvu });
      saldoRequestByCvu.set(cvu, requestPromise);
    }

    const response = await requestPromise;

    if (isCancelled()) return;

    let saldo: string | null = null;
    if (typeof response === "string") {
      saldo = response;
    } else if (
      response &&
      typeof response === "object" &&
      "saldo" in response &&
      typeof (response as any).saldo === "string"
    ) {
      saldo = (response as any).saldo;
    }

    if (saldo) {
      setCvuBalance({
        cvu,
        saldo,
      });
    } else {
      setCvuBalance(null);
      setCvuBalanceError(true);
    }
  } catch {
    if (isCancelled()) return;
    saldoRequestByCvu.delete(cvu);
    setCvuBalance(null);
    setCvuBalanceError(true);
  } finally {
    if (!isCancelled()) setLoadingCVUBalance(false);
  }
};
